import {useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {useWorkspace} from '../hooks/useWorkspace';
import {TaskCard} from '../components/WorkComponents';
import {dataClient} from '../data/dataClient';

const {FiPlus, FiLayout, FiList, FiX} = FiIcons;
const COLUMNS = ['todo', 'doing', 'review', 'done'];

export default function Tasks() {
  const {tasks = [], plans = [], modules = []} = useWorkspace();

  const [statusFilter, setStatusFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [viewMode, setViewMode] = useState('board'); // 'board' | 'list'
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newModule, setNewModule] = useState('finance');
  const [newDue, setNewDue] = useState('');

  // Apply all 4 filters
  const filtered = tasks.filter((task) => {
    if (statusFilter !== 'all' && task.status !== statusFilter) return false;
    if (moduleFilter !== 'all' && task.module !== moduleFilter) return false;
    if (ownerFilter !== 'all' && task.ownerType !== ownerFilter) return false;
    if (planFilter !== 'all' && task.planId !== planFilter) return false;
    return true;
  });

  async function handleStatusChange(taskId, nextStatus) {
    await dataClient.update('tasks', taskId, {status: nextStatus});
  }

  async function handleCreateTask(e) {
    e.preventDefault();
    if (!newTitle.trim()) return;

    await dataClient.create('tasks', {
      title: newTitle.trim(),
      status: 'todo',
      module: newModule,
      due: newDue || new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
      ownerType: 'human',
      owner: 'You'
    });

    setNewTitle('');
    setNewDue('');
    setShowCreate(false);
  }

  return (
    <div className="page">
      <header className="page-header">
        <span className="eyebrow">Tasks</span>
        <div className="inline spread">
          <div>
            <h1>Work moving forward.</h1>
            <p>Filterable across status, module, owner, and source plan. Click any status chip to advance.</p>
          </div>
          <div className="inline">
            <button
              type="button"
              className={`icon-button ${viewMode === 'board' ? 'active' : ''}`}
              onClick={() => setViewMode('board')}
              title="Board view"
              aria-label="Board view"
            >
              <SafeIcon icon={FiLayout} />
            </button>
            <button
              type="button"
              className={`icon-button ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List view"
              aria-label="List view"
            >
              <SafeIcon icon={FiList} />
            </button>
            <button
              type="button"
              className="button primary compact"
              onClick={() => setShowCreate(true)}
            >
              <SafeIcon icon={FiPlus} />
              New task
            </button>
          </div>
        </div>
      </header>

      {/* Filter Toolbar */}
      <section className="panel tasks-filter-bar" style={{marginBottom: '20px', padding: '14px 20px'}}>
        <div className="form-grid" style={{gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px'}}>
          <label>
            Status
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="todo">To do</option>
              <option value="doing">In progress</option>
              <option value="review">In review</option>
              <option value="done">Done</option>
            </select>
          </label>

          <label>
            Module
            <select value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}>
              <option value="all">All modules</option>
              {modules.map((m) => (
                <option key={m.slug} value={m.slug}>{m.name}</option>
              ))}
            </select>
          </label>

          <label>
            Owner
            <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}>
              <option value="all">All owners</option>
              <option value="bot">AI Bots only</option>
              <option value="human">Human team only</option>
            </select>
          </label>

          <label>
            Source Plan
            <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
              <option value="all">All plans</option>
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>{plan.title}</option>
              ))}
            </select>
          </label>
        </div>
      </section>

      {/* Quick Create Task Modal */}
      {showCreate && (
        <div className="add-page-modal-backdrop" onClick={() => setShowCreate(false)}>
          <div className="add-page-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="section-heading">
              <h3>Create new task</h3>
              <button type="button" className="icon-button" onClick={() => setShowCreate(false)}>
                <SafeIcon icon={FiX} />
              </button>
            </div>
            <form onSubmit={handleCreateTask}>
              <label className="filter">
                Task title
                <input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="e.g. Implement autumn campaign variants"
                  required
                />
              </label>
              <label className="filter" style={{marginTop: '12px'}}>
                Module
                <select value={newModule} onChange={(e) => setNewModule(e.target.value)}>
                  {modules.map((m) => (
                    <option key={m.slug} value={m.slug}>{m.name}</option>
                  ))}
                </select>
              </label>
              <label className="filter" style={{marginTop: '12px'}}>
                Due date
                <input
                  type="date"
                  value={newDue}
                  onChange={(e) => setNewDue(e.target.value)}
                />
              </label>
              <div className="wizard-actions">
                <button type="button" className="button ghost" onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
                <button type="submit" className="button primary" disabled={!newTitle.trim()}>
                  Add task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main View: Board vs List */}
      {viewMode === 'board' ? (
        <div className="task-board">
          {COLUMNS.map((column) => {
            const colTasks = filtered.filter((task) => task.status === column);
            return (
              <section key={column} className="board-column">
                <div className="section-heading">
                  <h3 style={{textTransform: 'capitalize'}}>{column}</h3>
                  <span className="count">{colTasks.length}</span>
                </div>
                {colTasks.map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    plan={plans.find((p) => p.id === task.planId)}
                    onStatusChange={handleStatusChange}
                  />
                ))}
              </section>
            );
          })}
        </div>
      ) : (
        <section className="panel">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Status</th>
                  <th>Module</th>
                  <th>Owner</th>
                  <th>Due</th>
                  <th>Source Plan</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((task) => {
                  const plan = plans.find((p) => p.id === task.planId);
                  return (
                    <tr key={task.id}>
                      <td><b>{task.title}</b></td>
                      <td>
                        <button
                          type="button"
                          className={`status ${task.status} status-clickable`}
                          onClick={() => {
                            const next = {todo: 'doing', doing: 'review', review: 'done', done: 'todo'}[task.status] || 'doing';
                            handleStatusChange(task.id, next);
                          }}
                        >
                          {task.status}
                        </button>
                      </td>
                      <td><small>{task.module || 'tasks'}</small></td>
                      <td>{task.owner || 'Unassigned'}</td>
                      <td><small>{task.due || task.due_date || '—'}</small></td>
                      <td>
                        {plan ? (
                          <small><b>{plan.title}</b></small>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
